// Layout modes. Every mode consumes the same document and the same field
// sampler. Most modes emit placements (centers plus an optional inherent
// rotation); Voronoi emits finished cell polygons instead.

import { Delaunay } from 'd3-delaunay'
import pc from 'polygon-clipping'
import type { DocumentModel, Pt, Ring, Poly } from './types'
import { MAX_INSTANCES } from './types'
import type { FieldSampler } from './fields'
import { makeRng, type Rng } from './rng'
import { boundaryBBox } from './boundary'
import type { BBox } from './geometry'
import { centroid, convexInset, ensureWinding, roundConvexCorners, signedArea } from './geometry'
import { sampleCatmullRom } from './curves'

export interface Placement {
  x: number
  y: number
  rot: number // inherent rotation from the mode, radians
}

// Upper bound on acceleration-grid cells for scatter and flow (memory guard).
const GRID_CELL_CAP = 1 << 22

export type ModeOutput =
  | { kind: 'placements'; placements: Placement[]; capped: boolean }
  | { kind: 'polys'; polys: Poly[]; capped: boolean }

export function runMode(doc: DocumentModel, fields: FieldSampler, boundary: Ring): ModeOutput {
  const rng = makeRng(doc.layout.seed)
  const bbox = boundaryBBox(doc)
  switch (doc.layout.mode) {
    case 'grid':
      return placements(gridMode(doc, fields, bbox))
    case 'scatter':
      return placements(scatterMode(fields, bbox, rng))
    case 'spiral':
      return placements(spiralMode(doc, fields, bbox))
    case 'concentric':
      return placements(concentricMode(doc, fields, bbox))
    case 'fibonacci':
      return placements(fibonacciMode(fields, bbox))
    case 'crosshatch':
      return placements(crosshatchMode(doc, fields, bbox))
    case 'voronoi':
      return voronoiMode(doc, fields, bbox, boundary, rng)
    case 'path':
      return placements(pathMode(doc, fields))
    case 'flowlines':
      return flowMode(doc, fields, bbox, boundary)
  }
}

// Step along each user path by arc length, with the step driven by the local
// spacing field and rotation following the tangent when rotateToPath is set.
function pathMode(doc: DocumentModel, f: FieldSampler): Accum {
  const acc: Accum = { list: [], capped: false }
  const rotate = doc.layout.rotateToPath
  for (const path of doc.layout.paths) {
    if (path.nodes.length < 2) continue
    const pts = path.kind === 'curve' ? sampleCatmullRom(path.nodes) : path.nodes
    // Cumulative arc length over the sampled polyline.
    const cum: number[] = [0]
    for (let i = 1; i < pts.length; i++) {
      cum.push(cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y))
    }
    const total = cum[cum.length - 1]
    if (total <= 0) continue

    let seg = 0
    const at = (s: number): { x: number; y: number; rot: number } => {
      while (seg + 1 < cum.length - 1 && cum[seg + 1] < s) seg++
      const a = pts[seg]
      const b = pts[seg + 1]
      const span = cum[seg + 1] - cum[seg]
      const t = span > 0 ? (s - cum[seg]) / span : 0
      return {
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
        rot: Math.atan2(b.y - a.y, b.x - a.x),
      }
    }

    let s = 0
    for (;;) {
      const p = at(Math.min(s, total))
      if (!push(acc, p.x, p.y, rotate ? p.rot : 0)) return acc
      if (s >= total) break
      const step = Math.max(0.05, f.spacing(p.x, p.y))
      // Land exactly on the endpoint when the next step would pass it.
      s = s + step > total && s < total ? total : s + step
    }
  }
  return acc
}

function placements(r: { list: Placement[]; capped: boolean }): ModeOutput {
  return { kind: 'placements', placements: r.list, capped: r.capped }
}

interface Accum {
  list: Placement[]
  capped: boolean
}

function push(acc: Accum, x: number, y: number, rot = 0): boolean {
  if (acc.list.length >= MAX_INSTANCES) {
    acc.capped = true
    return false
  }
  acc.list.push({ x, y, rot })
  return true
}

function gridMode(doc: DocumentModel, f: FieldSampler, bbox: BBox): Accum {
  const acc: Accum = { list: [], capped: false }
  const hex = doc.layout.grid.pattern === 'hex'
  const spinRow = (doc.layout.grid.crossRowSpin * Math.PI) / 180
  const spinBy = (doc.layout.grid.spinBy * Math.PI) / 180
  const latAngle = (doc.layout.grid.angle * Math.PI) / 180

  if (latAngle === 0) {
    // Axis-aligned lattice: the original traversal, kept verbatim so existing
    // documents reproduce identical patterns.
    const cx = (bbox.minX + bbox.maxX) / 2
    let y = bbox.minY
    let row = 0
    while (y <= bbox.maxY) {
      const rowStep = f.spacing(cx, y) * (hex ? Math.sqrt(3) / 2 : 1)
      const offset = hex && row % 2 === 1 ? f.spacing(bbox.minX, y) / 2 : 0
      let x = bbox.minX + offset
      while (x <= bbox.maxX) {
        // Cross-row spin accumulates per row; spin-by per placed instance.
        if (!push(acc, x, y, row * spinRow + acc.list.length * spinBy)) return acc
        x += f.spacing(x, y)
      }
      y += rowStep
      row++
    }
    return acc
  }

  // Rotated lattice: walk a frame large enough to cover the boundary at any
  // rotation and map lattice coordinates back into the document. Spacing is
  // sampled at the mapped point so gradient fields stay position-true, and
  // instances inherit the lattice rotation.
  const ccx = (bbox.minX + bbox.maxX) / 2
  const ccy = (bbox.minY + bbox.maxY) / 2
  const R = Math.hypot(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY) / 2
  const cos = Math.cos(latAngle)
  const sin = Math.sin(latAngle)
  const map = (u: number, v: number): Pt => [ccx + u * cos - v * sin, ccy + u * sin + v * cos]

  let v = -R
  let row = 0
  while (v <= R) {
    const [rx, ry] = map(0, v)
    const rowStep = f.spacing(rx, ry) * (hex ? Math.sqrt(3) / 2 : 1)
    const [ox, oy] = map(-R, v)
    const offset = hex && row % 2 === 1 ? f.spacing(ox, oy) / 2 : 0
    let u = -R + offset
    while (u <= R) {
      const [x, y] = map(u, v)
      if (bboxContains(bbox, x, y)) {
        if (!push(acc, x, y, latAngle + row * spinRow + acc.list.length * spinBy)) return acc
      }
      u += f.spacing(x, y)
    }
    v += rowStep
    row++
  }
  return acc
}

function bboxContains(bbox: BBox, x: number, y: number): boolean {
  return x >= bbox.minX && x <= bbox.maxX && y >= bbox.minY && y <= bbox.maxY
}

// Poisson-disk (Bridson) with the disk radius driven by the spacing field.
function scatterMode(f: FieldSampler, bbox: BBox, rng: Rng): Accum {
  const acc: Accum = { list: [], capped: false }
  const w = bbox.maxX - bbox.minX
  const h = bbox.maxY - bbox.minY
  if (w <= 0 || h <= 0) return acc

  // Coarse minimum of the spacing field for the acceleration grid.
  let rMin = Infinity
  for (let i = 0; i <= 8; i++) {
    for (let j = 0; j <= 8; j++) {
      rMin = Math.min(rMin, f.spacing(bbox.minX + (w * i) / 8, bbox.minY + (h * j) / 8))
    }
  }
  // Sanity: cap the acceleration grid's cell count (huge canvas + tiny
  // spacing would otherwise allocate tens of millions of cells before any
  // instance cap can help). A coarser grid stays correct, just scans more
  // points per cell; cells allocate lazily.
  let cell = rMin / Math.SQRT2
  if ((w / cell) * (h / cell) > GRID_CELL_CAP) cell = Math.sqrt((w * h) / GRID_CELL_CAP)
  const gw = Math.max(1, Math.ceil(w / cell))
  const gh = Math.max(1, Math.ceil(h / cell))
  const grid: (number[] | undefined)[] = new Array(gw * gh)
  const pts: Pt[] = []
  const radii: number[] = []

  const gridIndex = (x: number, y: number) => {
    const gx = Math.min(gw - 1, Math.max(0, Math.floor((x - bbox.minX) / cell)))
    const gy = Math.min(gh - 1, Math.max(0, Math.floor((y - bbox.minY) / cell)))
    return gy * gw + gx
  }

  const fits = (x: number, y: number, r: number): boolean => {
    const reach = Math.ceil((r + rMin) / cell) + 1
    const gx = Math.floor((x - bbox.minX) / cell)
    const gy = Math.floor((y - bbox.minY) / cell)
    for (let j = Math.max(0, gy - reach); j <= Math.min(gh - 1, gy + reach); j++) {
      for (let i = Math.max(0, gx - reach); i <= Math.min(gw - 1, gx + reach); i++) {
        const bucket = grid[j * gw + i]
        if (!bucket) continue
        for (const idx of bucket) {
          const [px, py] = pts[idx]
          const minDist = (r + radii[idx]) / 2
          if (Math.hypot(px - x, py - y) < minDist) return false
        }
      }
    }
    return true
  }

  const add = (x: number, y: number, r: number) => {
    const idx = pts.length
    pts.push([x, y])
    radii.push(r)
    const gi = gridIndex(x, y)
    ;(grid[gi] ??= []).push(idx)
    active.push(idx)
  }

  const active: number[] = []
  const sx = bbox.minX + w / 2
  const sy = bbox.minY + h / 2
  add(sx, sy, f.spacing(sx, sy))

  const K = 30
  while (active.length > 0 && pts.length < MAX_INSTANCES) {
    const pick = Math.floor(rng() * active.length)
    const idx = active[pick]
    const [px, py] = pts[idx]
    const pr = radii[idx]
    let placed = false
    for (let k = 0; k < K; k++) {
      const ang = rng() * Math.PI * 2
      const dist = pr * (1 + rng())
      const x = px + Math.cos(ang) * dist
      const y = py + Math.sin(ang) * dist
      if (x < bbox.minX || x > bbox.maxX || y < bbox.minY || y > bbox.maxY) continue
      const r = f.spacing(x, y)
      if (!fits(x, y, r)) continue
      add(x, y, r)
      placed = true
      break
    }
    if (!placed) {
      active[pick] = active[active.length - 1]
      active.pop()
    }
  }
  if (pts.length >= MAX_INSTANCES) acc.capped = true
  // Boundary culling happens later in the pipeline; keep all points here.
  for (const [x, y] of pts) {
    if (!push(acc, x, y)) break
  }
  return acc
}

function spiralMode(doc: DocumentModel, f: FieldSampler, bbox: BBox): Accum {
  const acc: Accum = { list: [], capped: false }
  const cx = (bbox.minX + bbox.maxX) / 2
  const cy = (bbox.minY + bbox.maxY) / 2
  const maxR = Math.hypot(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY) / 2
  const rotate = doc.layout.rotateToPath
  let theta = 0
  let r = f.spacing(cx, cy) / 2 // skip the degenerate center point
  while (r <= maxR) {
    const x = cx + r * Math.cos(theta)
    const y = cy + r * Math.sin(theta)
    const s = f.spacing(x, y)
    if (!push(acc, x, y, rotate ? theta + Math.PI / 2 : 0)) return acc
    const b = s / (2 * Math.PI) // radial growth per radian for pitch = spacing
    const dTheta = s / Math.max(r, s / 2)
    theta += dTheta
    r += b * dTheta
  }
  return acc
}

function concentricMode(doc: DocumentModel, f: FieldSampler, bbox: BBox): Accum {
  const acc: Accum = { list: [], capped: false }
  const cx = (bbox.minX + bbox.maxX) / 2
  const cy = (bbox.minY + bbox.maxY) / 2
  const maxR = Math.hypot(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY) / 2
  const rotate = doc.layout.rotateToPath
  if (!push(acc, cx, cy)) return acc
  let r = f.spacing(cx, cy)
  let ringIdx = 0
  while (r <= maxR) {
    // Average spacing around the ring decides the point count.
    let sAvg = 0
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2
      sAvg += f.spacing(cx + r * Math.cos(a), cy + r * Math.sin(a))
    }
    sAvg /= 8
    const n = Math.max(1, Math.round((2 * Math.PI * r) / sAvg))
    const stagger = ringIdx % 2 === 1 ? Math.PI / n : 0
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + stagger
      const x = cx + r * Math.cos(a)
      const y = cy + r * Math.sin(a)
      if (!push(acc, x, y, rotate ? a + Math.PI / 2 : 0)) return acc
    }
    r += f.spacing(cx + r, cy)
    ringIdx++
  }
  return acc
}

function fibonacciMode(f: FieldSampler, bbox: BBox): Accum {
  const acc: Accum = { list: [], capped: false }
  const cx = (bbox.minX + bbox.maxX) / 2
  const cy = (bbox.minY + bbox.maxY) / 2
  const maxR = Math.hypot(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY) / 2
  const golden = Math.PI * (3 - Math.sqrt(5)) // 137.508 degrees
  let n = 1
  let lastX = cx
  let lastY = cy
  for (;;) {
    // Vogel phyllotaxis with the density constant driven by the local spacing.
    const s = f.spacing(lastX, lastY)
    const c = 0.564 * s
    const r = c * Math.sqrt(n)
    if (r > maxR) break
    const a = n * golden
    const x = cx + r * Math.cos(a)
    const y = cy + r * Math.sin(a)
    if (!push(acc, x, y)) return acc
    lastX = x
    lastY = y
    n++
    if (n > MAX_INSTANCES * 2) break
  }
  return acc
}

function crosshatchMode(doc: DocumentModel, f: FieldSampler, bbox: BBox): Accum {
  const acc: Accum = { list: [], capped: false }
  const cx = (bbox.minX + bbox.maxX) / 2
  const cy = (bbox.minY + bbox.maxY) / 2
  const maxD = Math.hypot(bbox.maxX - bbox.minX, bbox.maxY - bbox.minY) / 2

  const offsetsFor = (angleDeg: number): number[] => {
    const a = (angleDeg * Math.PI) / 180
    const nx = -Math.sin(a)
    const ny = Math.cos(a)
    const out: number[] = [0]
    for (const dir of [1, -1]) {
      let t = 0
      for (;;) {
        const s = f.spacing(cx + nx * t * dir, cy + ny * t * dir)
        t += s
        if (t > maxD) break
        out.push(t * dir)
      }
    }
    return out
  }

  const aA = (doc.layout.crosshatch.angleA * Math.PI) / 180
  const aB = (doc.layout.crosshatch.angleB * Math.PI) / 180
  const nA: Pt = [-Math.sin(aA), Math.cos(aA)]
  const nB: Pt = [-Math.sin(aB), Math.cos(aB)]
  const det = nA[0] * nB[1] - nA[1] * nB[0]
  if (Math.abs(det) < 1e-6) return acc // parallel families produce no intersections

  const offsA = offsetsFor(doc.layout.crosshatch.angleA)
  const offsB = offsetsFor(doc.layout.crosshatch.angleB)
  for (const ta of offsA) {
    for (const tb of offsB) {
      // Solve dot(p - c, nA) = ta, dot(p - c, nB) = tb.
      const dx = (ta * nB[1] - tb * nA[1]) / det
      const dy = (tb * nA[0] - ta * nB[0]) / det
      const x = cx + dx
      const y = cy + dy
      if (x < bbox.minX || x > bbox.maxX || y < bbox.minY || y > bbox.maxY) continue
      if (!push(acc, x, y)) return acc
    }
  }
  return acc
}

function voronoiMode(
  doc: DocumentModel,
  f: FieldSampler,
  bbox: BBox,
  boundary: Ring,
  rng: Rng,
): ModeOutput {
  const seeds = scatterMode(f, bbox, rng)
  const pts = seeds.list.map((p) => [p.x, p.y] as Pt)
  if (pts.length < 3) return { kind: 'polys', polys: [], capped: seeds.capped }
  const pad = 1
  const delaunay = Delaunay.from(pts)
  const voronoi = delaunay.voronoi([bbox.minX - pad, bbox.minY - pad, bbox.maxX + pad, bbox.maxY + pad])
  const concave = doc.boundary.kind === 'polygon'
  const polys: Poly[] = []
  for (let i = 0; i < pts.length; i++) {
    const cell = voronoi.cellPolygon(i)
    if (!cell || cell.length < 4) continue
    let ring: Ring = cell.slice(0, -1).map((p) => [p[0], p[1]])
    ring = ensureWinding(ring, true)
    if (!concave) {
      // Convex boundary (rect or tessellated circle): clip with
      // Sutherland-Hodgman, then inset by half the local crack width (the
      // size field drives crack width in this mode).
      ring = clipConvexToBoundary(ring, boundary)
      if (ring.length < 3) continue
      const [ccx, ccy] = centroid(ring)
      const crack = Math.max(0.1, f.size(ccx, ccy))
      const inset = convexInset(ring, crack / 2)
      if (inset && inset.length >= 3) {
        const rounded = roundConvexCorners(inset, doc.layout.voronoi.cornerRadius, doc.export.tolerance)
        polys.push([rounded])
      }
      continue
    }
    // Concave (polygon) boundary: inset the still-convex cell first, then
    // intersect with the boundary polygon. Cells meeting the boundary are
    // cut flush along it.
    const [ccx, ccy] = centroid(ring)
    const crack = Math.max(0.1, f.size(ccx, ccy))
    const inset = convexInset(ring, crack / 2)
    if (!inset || inset.length < 3) continue
    const rounded = roundConvexCorners(inset, doc.layout.voronoi.cornerRadius, doc.export.tolerance)
    let parts: [number, number][][][]
    try {
      parts = pc.intersection([[rounded as [number, number][]]], [[boundary as [number, number][]]])
    } catch {
      continue
    }
    for (const poly of parts) {
      const rings: Ring[] = []
      for (let k = 0; k < poly.length; k++) {
        let r = poly[k] as Ring
        const first = r[0]
        const last = r[r.length - 1]
        if (r.length > 1 && first[0] === last[0] && first[1] === last[1]) r = r.slice(0, -1)
        if (r.length < 3 || Math.abs(signedArea(r)) < 1e-6) continue
        rings.push(ensureWinding(r, k === 0))
      }
      if (rings.length > 0) polys.push(rings)
    }
  }
  return { kind: 'polys', polys, capped: seeds.capped }
}

// Flow lines: evenly spaced streamlines through the angle field
// (Jobard-Lefer seeding, RK2 integration), stroked to outlines with the
// width from the size field. Outlines are unions of variable-width segment
// trapezoids plus joint/cap circles, then clipped to the boundary, so caps
// at the boundary follow the true clipped edge exactly.
function flowMode(doc: DocumentModel, f: FieldSampler, bbox: BBox, boundary: Ring): ModeOutput {
  const w = bbox.maxX - bbox.minX
  const hgt = bbox.maxY - bbox.minY
  if (w <= 0 || hgt <= 0) return { kind: 'polys', polys: [], capped: false }

  // Coarse minimum separation for the occupancy grid and step size.
  let dsepMin = Infinity
  for (let i = 0; i <= 8; i++) {
    for (let j = 0; j <= 8; j++) {
      dsepMin = Math.min(dsepMin, f.spacing(bbox.minX + (w * i) / 8, bbox.minY + (hgt * j) / 8))
    }
  }
  dsepMin = Math.max(0.8, dsepMin)
  const h = Math.min(1, dsepMin / 3) // integration step
  // Same sanity cap as scatter: never allocate an unbounded occupancy grid.
  let cell = dsepMin / 2
  if ((w / cell) * (hgt / cell) > GRID_CELL_CAP) cell = Math.sqrt((w * hgt) / GRID_CELL_CAP)
  const gw = Math.max(1, Math.ceil(w / cell))
  const gh = Math.max(1, Math.ceil(hgt / cell))
  const occ: ({ x: number; y: number; line: number }[] | undefined)[] = new Array(gw * gh)

  const cellIndex = (x: number, y: number) => {
    const gx = Math.min(gw - 1, Math.max(0, Math.floor((x - bbox.minX) / cell)))
    const gy = Math.min(gh - 1, Math.max(0, Math.floor((y - bbox.minY) / cell)))
    return gy * gw + gx
  }

  // Distance to the nearest accepted point of any OTHER streamline.
  const clearance = (x: number, y: number, ignoreLine: number, within: number): number => {
    const reach = Math.ceil(within / cell) + 1
    const gx = Math.floor((x - bbox.minX) / cell)
    const gy = Math.floor((y - bbox.minY) / cell)
    let best = Infinity
    for (let j = Math.max(0, gy - reach); j <= Math.min(gh - 1, gy + reach); j++) {
      for (let i = Math.max(0, gx - reach); i <= Math.min(gw - 1, gx + reach); i++) {
        const bucket = occ[j * gw + i]
        if (!bucket) continue
        for (const p of bucket) {
          if (p.line === ignoreLine) continue
          const d = Math.hypot(p.x - x, p.y - y)
          if (d < best) best = d
        }
      }
    }
    return best
  }

  const dirAt = (x: number, y: number): Pt => {
    const a = (f.angle(x, y) * Math.PI) / 180
    return [Math.cos(a), Math.sin(a)]
  }
  const inBox = (x: number, y: number) =>
    x >= bbox.minX - h && x <= bbox.maxX + h && y >= bbox.minY - h && y <= bbox.maxY + h

  const MAX_POINTS = 60000
  const MAX_LINE_STEPS = 4000
  let totalPoints = 0
  let capped = false

  const lines: Pt[][] = []
  const seedQueue: Pt[] = [[(bbox.minX + bbox.maxX) / 2, (bbox.minY + bbox.maxY) / 2]]

  const traceHalf = (sx: number, sy: number, sign: 1 | -1, line: number): Pt[] => {
    const pts: Pt[] = []
    let [px, py] = [sx, sy]
    let [dx, dy] = dirAt(sx, sy)
    dx *= sign
    dy *= sign
    for (let step = 0; step < MAX_LINE_STEPS; step++) {
      // RK2 with direction continuity (flip samples that come back rotated
      // more than 90 degrees, so the field is a line field, not a vector field).
      let [k1x, k1y] = dirAt(px, py)
      if (k1x * dx + k1y * dy < 0) {
        k1x = -k1x
        k1y = -k1y
      }
      let [k2x, k2y] = dirAt(px + (k1x * h) / 2, py + (k1y * h) / 2)
      if (k2x * k1x + k2y * k1y < 0) {
        k2x = -k2x
        k2y = -k2y
      }
      const nx = px + k2x * h
      const ny = py + k2y * h
      if (!inBox(nx, ny)) break
      const dtest = Math.max(0.6, f.spacing(nx, ny)) * 0.5
      if (clearance(nx, ny, line, dtest) < dtest) break
      pts.push([nx, ny])
      px = nx
      py = ny
      dx = k2x
      dy = k2y
    }
    return pts
  }

  while (seedQueue.length > 0 && totalPoints < MAX_POINTS) {
    const [sx, sy] = seedQueue.shift()!
    if (!inBox(sx, sy)) continue
    const dsep = Math.max(0.8, f.spacing(sx, sy))
    if (clearance(sx, sy, -1, dsep * 0.9) < dsep * 0.9) continue

    const line = lines.length
    const back = traceHalf(sx, sy, -1, line).reverse()
    const fwd = traceHalf(sx, sy, 1, line)
    const pts: Pt[] = [...back, [sx, sy], ...fwd]
    if (pts.length < 4) continue

    for (const [x, y] of pts) {
      const ci = cellIndex(x, y)
      ;(occ[ci] ??= []).push({ x, y, line })
    }
    totalPoints += pts.length
    lines.push(pts)

    // Candidate seeds offset to both sides at the local separation.
    for (let i = 0; i < pts.length; i += Math.max(2, Math.round(dsepMin / h))) {
      const [x, y] = pts[i]
      const [tx, ty] = dirAt(x, y)
      const off = Math.max(0.8, f.spacing(x, y)) * 1.05
      seedQueue.push([x - ty * off, y + tx * off])
      seedQueue.push([x + ty * off, y - tx * off])
    }
  }
  if (totalPoints >= MAX_POINTS) capped = true

  // Stroke each streamline to an outline. The spike showed that unioning
  // hundreds of exactly-abutting capsule pieces trips Martinez degeneracies,
  // so strokes are built directly as one ribbon polygon (offset sides with
  // clamped miters, sampled arc caps); a single self-union per line cleans
  // any fold, and the boundary intersection cuts caps along the true edge.
  const flat = doc.layout.flow.cap === 'flat'
  const tol = doc.export.tolerance
  const polys: Poly[] = []
  const half = (x: number, y: number) => Math.max(0.1, f.size(x, y) / 2)
  for (const pts of lines) {
    const line = decimate(pts, Math.max(h, 0.6))
    if (line.length < 2) continue
    const ribbon = strokeRibbon(line, half, flat, tol)
    if (!ribbon) continue

    let clipped: [number, number][][][]
    try {
      const cleaned = pc.union([ribbon as [number, number][]])
      clipped = pc.intersection(cleaned, [[boundary as [number, number][]]])
    } catch {
      continue // a pathological ribbon must never kill generation
    }
    for (const poly of clipped) {
      const rings: Ring[] = []
      for (let i = 0; i < poly.length; i++) {
        let ring = poly[i] as Ring
        const first = ring[0]
        const last = ring[ring.length - 1]
        if (ring.length > 1 && first[0] === last[0] && first[1] === last[1]) ring = ring.slice(0, -1)
        if (ring.length < 3 || Math.abs(signedArea(ring)) < 1e-6) continue
        rings.push(ensureWinding(ring, i === 0))
      }
      if (rings.length > 0) polys.push(rings)
    }
  }
  return { kind: 'polys', polys, capped }
}

// Offset outline of a variable-width polyline: left side forward, arc (or
// flat) end cap, right side backward, arc start cap.
function strokeRibbon(
  line: Pt[],
  half: (x: number, y: number) => number,
  flat: boolean,
  tol: number,
): Ring | null {
  const n = line.length
  if (n < 2) return null
  // Per-point unit normals from averaged segment directions, miter-clamped.
  const normals: Pt[] = []
  const widths: number[] = []
  for (let i = 0; i < n; i++) {
    const [px, py] = line[Math.max(0, i - 1)]
    const [qx, qy] = line[Math.min(n - 1, i + 1)]
    const len = Math.hypot(qx - px, qy - py)
    if (len < 1e-9) return null
    const dx = (qx - px) / len
    const dy = (qy - py) / len
    // Miter scale from the angle to the incoming segment, clamped at 2.
    let scale = 1
    if (i > 0 && i < n - 1) {
      const [ax, ay] = line[i - 1]
      const [bx, by] = line[i]
      const l1 = Math.hypot(bx - ax, by - ay)
      if (l1 > 1e-9) {
        const cos = Math.max(0.5, dx * ((bx - ax) / l1) + dy * ((by - ay) / l1))
        scale = Math.min(2, 1 / cos)
      }
    }
    normals.push([-dy * scale, dx * scale])
    widths.push(half(line[i][0], line[i][1]))
  }

  const ring: Ring = []
  for (let i = 0; i < n; i++) {
    ring.push([line[i][0] + normals[i][0] * widths[i], line[i][1] + normals[i][1] * widths[i]])
  }
  appendCap(ring, line[n - 1], normals[n - 1], widths[n - 1], flat, tol)
  for (let i = n - 1; i >= 0; i--) {
    ring.push([line[i][0] - normals[i][0] * widths[i], line[i][1] - normals[i][1] * widths[i]])
  }
  appendCap(ring, line[0], [-normals[0][0], -normals[0][1]], widths[0], flat, tol)
  return ring
}

// Sweep a half-circle from the +normal offset to the -normal offset (through
// the forward direction). Flat caps skip the arc, the ribbon just closes.
function appendCap(ring: Ring, p: Pt, normal: Pt, w: number, flat: boolean, tol: number) {
  if (flat) return
  const nl = Math.hypot(normal[0], normal[1])
  if (nl < 1e-9 || w < 1e-6) return
  const a0 = Math.atan2(normal[1] / nl, normal[0] / nl)
  const steps = Math.max(3, Math.ceil(Math.PI / (2 * Math.acos(Math.max(0.1, 1 - Math.min(tol, w * 0.5) / w)))))
  for (let s = 1; s < steps; s++) {
    const a = a0 - (Math.PI * s) / steps
    ring.push([p[0] + w * Math.cos(a), p[1] + w * Math.sin(a)])
  }
}

// Keep points that advance at least minDist or bend more than ~4 degrees.
function decimate(pts: Pt[], minDist: number): Pt[] {
  if (pts.length <= 2) return pts
  const out: Pt[] = [pts[0]]
  let [lx, ly] = pts[0]
  let lastDir: Pt | null = null
  for (let i = 1; i < pts.length - 1; i++) {
    const [x, y] = pts[i]
    const d = Math.hypot(x - lx, y - ly)
    if (d < 1e-9) continue
    const dir: Pt = [(x - lx) / d, (y - ly) / d]
    const bent = lastDir !== null && dir[0] * lastDir[0] + dir[1] * lastDir[1] < 0.9976
    if (d >= minDist || bent) {
      out.push([x, y])
      lx = x
      ly = y
      lastDir = dir
    }
  }
  out.push(pts[pts.length - 1])
  return out
}

function clipConvexToBoundary(ring: Ring, boundary: Ring): Ring {
  // Sutherland-Hodgman against each boundary edge. The boundary is convex
  // (rect or tessellated circle) and normalized CCW.
  let poly = ring
  const n = boundary.length
  for (let i = 0; i < n && poly.length >= 3; i++) {
    const [ax, ay] = boundary[i]
    const [bx, by] = boundary[(i + 1) % n]
    const ex = bx - ax
    const ey = by - ay
    const len = Math.hypot(ex, ey)
    if (len < 1e-12) continue
    const nx = -ey / len // inward normal of a CCW ring
    const ny = ex / len
    const out: Ring = []
    for (let j = 0; j < poly.length; j++) {
      const [px, py] = poly[j]
      const [qx, qy] = poly[(j + 1) % poly.length]
      const dp = (px - ax) * nx + (py - ay) * ny
      const dq = (qx - ax) * nx + (qy - ay) * ny
      if (dp >= 0) out.push([px, py])
      if ((dp >= 0) !== (dq >= 0)) {
        const t = dp / (dp - dq)
        out.push([px + (qx - px) * t, py + (qy - py) * t])
      }
    }
    poly = out
  }
  return poly
}
